import { useState, useEffect } from 'react';
import { axios } from '../../../../utils/axios';
import { Modal, Group, TextInput, Textarea, Button, Text } from '@mantine/core';
import { showAlert } from '../../../../utils/alerts';
export const checkDiff = (main, reference) => {
  const diff =
    parseFloat(main) && parseFloat(reference) ? parseFloat(main) !== parseFloat(reference) : false;
  const difference =
    parseFloat(main) && parseFloat(reference) ? parseFloat(main) - parseFloat(reference) : 0;
  return { diff: diff, variance: parseFloat(difference).toFixed(2) };
};

export const checkIfFlagged = (requested, primary) => {
  if (!primary) return false;
  return (
    parseFloat(requested?.quantity ?? '') !== parseFloat(primary?.quantity ?? '') ||
    parseFloat(requested?.charge_quantity ?? '') !== parseFloat(primary?.charge_quantity ?? '') ||
    parseFloat(requested?.operation_quantity ?? '') !==
      parseFloat(primary?.operation_quantity ?? '') ||
    parseFloat(requested?.activity_type ?? '') !== parseFloat(primary?.activity_type ?? '')
  );
};

const getVariance = (main = 0, ref = 0) => {
  const variance = parseFloat(main) - parseFloat(ref);
  return (variance < 0 ? -variance : variance).toFixed(3);
};

const getFinalValue = (main, ref, checkVar) => {
  const parseMain = main ?? 'N/A';
  const parsedRef = ref ?? 'N/A';

  if (checkVar) {
    const mainCost = parseMain !== 'N/A' ? parseFloat(main).toFixed(3) : 'N/A';
    const refCost = parsedRef !== 'N/A' ? parseFloat(ref).toFixed(3) : 'N/A';
    const variance = mainCost !== 'N/A' && refCost !== 'N/A' ? getVariance(main, ref) : 'N/A';
    return [`Main: ${mainCost}`, `Ref: ${refCost}`, `Var: ${variance}`].join(',');
  } else {
    const mainCost = parseMain !== 'N/A' ? main : 'N/A';
    const refCost = parsedRef !== 'N/A' ? ref : 'N/A';
    return [`Main: ${mainCost}`, `Ref: ${refCost}`].join(',');
  }
};

export const transformData = (apiData) => {
  return apiData.map((ele, index) => {
    const isFlagged = checkIfFlagged(ele, ele.reference_bom?.[0]);
    const hasReference = ele.reference_bom.length > 0 ? true : false;

    return {
      ...ele,
      flagged: isFlagged,
      hasReference: hasReference,
      itemModified: false,
      indexId: index,
      duration_excel: getFinalValue(ele?.quantity, ele?.reference_bom?.[0]?.quantity, true),
      unit_excel: getFinalValue(
        ele?.component_unit,
        ele?.reference_bom?.[0]?.component_unit,
        false,
      ),
      activity_type_excel: getFinalValue(
        ele?.activity_type,
        ele?.reference_bom?.[0]?.activity_type,
        false,
      ),
      charge_qty_excel: getFinalValue(
        ele?.charge_quantity,
        ele?.reference_bom?.[0]?.charge_quantity,
        true,
      ),
      charge_qty_unit_excel: getFinalValue(
        ele?.charge_quantity_unit,
        ele?.reference_bom?.[0]?.charge_quantity_unit,
        false,
      ),
      operation_qty_excel: getFinalValue(
        ele?.operation_quantity,
        ele?.reference_bom?.[0]?.operation_quantity,
        true,
      ),
      operation_qty_unit_excel: getFinalValue(
        ele?.operation_quantity_unit,
        ele?.reference_bom?.[0]?.operation_quantity_unit,
        false,
      ),
      total_value_excel: getFinalValue(
        ele?.total_value,
        ele?.reference_bom?.[0]?.total_value,
        true,
      ),
      price_excel: getFinalValue(
        ele?.RequestedSkuCostEstimation?.price,
        ele?.reference_bom?.[0]?.PrimaryRefSkuCostEstimation?.price,
        false,
      ),
      price_unit_excel: getFinalValue(
        ele?.RequestedSkuCostEstimation?.un,
        ele?.reference_bom?.[0]?.PrimaryRefSkuCostEstimation?.un,
        false,
      ),
      price_per_unit_excel: getFinalValue(
        ele?.RequestedSkuCostEstimation?.price_unit,
        ele?.reference_bom?.[0]?.PrimaryRefSkuCostEstimation?.price_unit,
        false,
      ),
    };
  });
};

export const formatFloat = (val) => {
  return parseFloat(val).toFixed(3) ?? 0;
};

export function AddChangeNumberPopup({ opened = true, onClose, crNumber, isBom }) {
  const [formData, setFormData] = useState({
    changeNumber: '',
    validFrom: '',
    description: '',
    reason: '',
  });

  const [dateModalOpened, setDateModalOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [successModal, setSuccessModal] = useState(false);

  //  Fetch existing change number if it exists
  useEffect(() => {
    const fetchChangeNumber = async () => {
      if (!crNumber) return;

      try {
        setFetching(true);
        const response = await axios.get(
          `/costing-request/change-number/${crNumber}?is_bom=${isBom}`,
        );

        if (response.data.success && response.data.data) {
          const record = response.data.data;
          setFormData({
            changeNumber: record.change_number || '',
            validFrom: record.valid_from || '',
            description: record.description || '',
            reason: record.reason_for_change || '',
          });
          setIsReadOnly(true);
        } else {
          setIsReadOnly(false);
        }
      } catch (error) {
        if (error.response?.status === 400) {
          setIsReadOnly(false);
        } else {
          showAlert({ message: error.response?.data?.message || error.message }, 'error');
        }
      } finally {
        setFetching(false);
      }
    };

    if (opened) fetchChangeNumber();
  }, [opened, crNumber]);

  //  Handle confirm button
  const handleConfirmChanges = async () => {
    try {
      if (!formData.validFrom) {
        showAlert({ message: 'Please enter both Change Number and Valid From date.' }, 'error');
        return;
      }

      setLoading(true);

      const payload = {
        valid_from: formData.validFrom,
        description: formData.description,
        reason_for_change: formData.reason,
        is_bom: isBom,
      };
      const response = await axios.post(
        `/costing-request/create-change-number/${crNumber}`,
        payload,
      );

      if (response.data.success) {
        setSuccessModal(true);
        setFormData({ ...formData, changeNumber: response?.data?.bapiResponse?.data?.Change_No });
        return;
      } else {
        showAlert({ message: response.data.message || 'Something went wrong.' }, 'error');
      }
    } catch (err) {
      showAlert({ message: err.response?.data?.message || err.message }, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        centered
        title={
          fetching ? null : (
            <strong>{isReadOnly ? 'View Change Number' : 'Add Change Number'}</strong>
          )
        }
      >
        {fetching ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>
        ) : (
          <>
            <Group grow mb='sm'>
              <TextInput
                label='Change number'
                placeholder='Change number'
                value={formData.changeNumber}
                disabled={false}
                onChange={(e) => setFormData({ ...formData, changeNumber: e.currentTarget.value })}
              />

              <TextInput
                label='Valid from'
                placeholder='Select date'
                disabled={isReadOnly}
                value={formData.validFrom || ''}
                onClick={() => {
                  if (!isReadOnly) setDateModalOpened(true);
                }}
              />
            </Group>

            <Textarea
              label='Description'
              placeholder='Enter here'
              mb='sm'
              value={formData.description}
              disabled={isReadOnly}
              onChange={(e) => setFormData({ ...formData, description: e.currentTarget.value })}
            />

            <Textarea
              label='Reason for change'
              placeholder='Enter reason for change'
              mb='lg'
              autosize
              minRows={2}
              value={formData.reason}
              disabled={isReadOnly}
              onChange={(e) => setFormData({ ...formData, reason: e.currentTarget.value })}
            />
            {!isReadOnly && (
              <Text size='sm' mb='md'>
                **The change number will be populated after confirmation
              </Text>
            )}
            {!isReadOnly && (
              <Button fullWidth color='dark' loading={loading} onClick={handleConfirmChanges}>
                Confirm Changes
              </Button>
            )}
          </>
        )}
      </Modal>
      <Modal
        opened={dateModalOpened}
        onClose={() => setDateModalOpened(false)}
        centered
        title='Select Date'
        size='sm'
      >
        <input
          type='date'
          style={{
            width: '100%',
            fontSize: '16px',
            padding: '8px',
            borderRadius: '8px',
            border: '1px solid #ccc',
            marginBottom: '1rem',
          }}
          value={formData.validFrom}
          onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
        />
        <Button fullWidth color='dark' onClick={() => setDateModalOpened(false)}>
          Confirm Date
        </Button>
      </Modal>
      <Modal
        opened={successModal}
        onClose={() => {
          setSuccessModal(false);
          if (onClose) onClose(); // close main modal too
        }}
        centered
        title={<strong>Change Number Created</strong>}
        size='sm'
      >
        <Text mb='sm'>
          Change number <strong>{formData.changeNumber || '—'}</strong> has been created
          successfully.
        </Text>

        <Button
          fullWidth
          color='dark'
          onClick={() => {
            setSuccessModal(false);
            if (onClose) onClose();
          }}
        >
          OK
        </Button>
      </Modal>
    </>
  );
}
